import { ReactNode, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, Spinner } from '../../components/common';
import { supabase } from '../../lib/supabase/client';

interface TeamAccessGuardProps {
  children: ReactNode;
}

export const TeamAccessGuard = ({ children }: TeamAccessGuardProps) => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    console.log('TeamAccessGuard: Checking access for teamId', teamId);
    const fullUrl = window.location.href;
    console.log('TeamAccessGuard: Full URL:', fullUrl);
    
    if (!teamId) {
      setIsLoading(false);
      setHasAccess(false);
      console.log('TeamAccessGuard: No teamId in params, denying access');
      return;
    }
    
    const checkAccess = async () => {
      try {
        console.log('TeamAccessGuard: Starting access check for team', teamId);
        setIsLoading(true);
        
        if (!supabase) {
          console.error('TeamAccessGuard: Supabase client not initialized');
          setIsLoading(false);
          setHasAccess(false);
          return;
        }
        
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          console.error('TeamAccessGuard: Session error', sessionError);
          setIsLoading(false);
          setHasAccess(false);
          return;
        }
        
        if (!sessionData?.session?.user) {
          console.error('TeamAccessGuard: No user session found');
          setIsLoading(false);
          setHasAccess(false);
          return;
        }
        
        const userId = sessionData.session.user.id;
        console.log('TeamAccessGuard: User ID from session:', userId);
        
        // Check the user's teams directly
        const { data: teams, error: teamsError } = await supabase
          .from('team_members')
          .select('team_id')
          .eq('user_id', userId);
        
        if (teamsError) {
          console.error('TeamAccessGuard: Error fetching user teams', teamsError);
          setIsLoading(false);
          setHasAccess(false);
          return;
        }
        
        console.log('TeamAccessGuard: User teams:', teams);
        
        // Check if the current teamId is in the user's teams
        const hasTeamAccess = teams.some(team => team.team_id === teamId);
        console.log('TeamAccessGuard: Has access to team:', hasTeamAccess, 'Current teamId:', teamId);
        
        // Use the actual access check result instead of forcing it
        setHasAccess(hasTeamAccess);
        setIsLoading(false);
        
        if (!hasTeamAccess) {
          console.log('TeamAccessGuard: No access, redirecting to dashboard');
          window.location.href = '/dashboard'; // Use direct browser navigation instead of Router navigation
        } else {
          console.log('TeamAccessGuard: Access granted');
        }
      } catch (err) {
        console.error('TeamAccessGuard: Unexpected error during access check', err);
        setError(err instanceof Error ? err.message : 'Failed to check team access');
        setIsLoading(false);
        setHasAccess(false);
        // TEMPORARY: Comment out redirect for debugging
        // navigate('/dashboard');
      }
    };
    
    checkAccess();
  }, [teamId, navigate]);
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }
  
  if (error) {
    return (
      <Alert variant="error" className="my-4">
        {error}
      </Alert>
    );
  }
  
  if (!hasAccess) {
    return (
      <Alert variant="error" className="my-4">
        You do not have access to this team. Redirecting to dashboard...
      </Alert>
    );
  }
  
  console.log('TeamAccessGuard: Rendering children');
  return <>{children}</>;
}; 